import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  compatibleModelEffort,
  modelEffortLevels,
  type AiEffort,
} from "@calcplan/ai-capabilities";
import {
  Bluetooth,
  Check,
  ChevronRight,
  CircleUserRound,
  Cloud,
  Database,
  Gauge,
  Languages,
  Link2,
  KeyRound,
  Palette,
  RotateCcw,
  Save,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  UserX,
} from "lucide-react";
import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  defaultSectionForScope,
  sectionIdsForScope,
  type SettingsScope,
  type SettingsSection,
} from "./settings-scope";

type AiProvider = "anthropic" | "openai";
interface AiSettings {
  provider: AiProvider;
  baseUrl: string;
  model: string;
  effort: AiEffort | null;
  /** Só o suficiente para reconhecer a chave — o valor nunca sai do servidor. */
  keyMask: string;
  hasKey: boolean;
  providers: Record<AiProvider, { label: string; baseUrl: string; models: string[] }>;
  mcpServers: Array<{
    id: string;
    name: string;
    url: string;
    enabled: boolean;
    allowedTools: string[];
    hasToken: boolean;
    tokenMask: string;
  }>;
  featureAvailability: {
    nativeToolSearch: boolean;
    serverContextManagement: boolean;
    externalMcp: boolean;
  };
  currentModelMetadata: AiModelInfo | null;
}

interface AiModelInfo {
  id: string;
  displayName: string;
  maxInputTokens: number | null;
  maxOutputTokens: number | null;
  supportsTools: boolean;
  supportsImages: boolean;
  supportsPdf: boolean;
  supportsContextManagement: boolean;
  supportedEfforts: AiEffort[];
}

interface McpServerForm {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
  allowedTools: string;
  authorizationToken: string;
  tokenMask?: string;
}

const effortLabels: Record<AiEffort, string> = {
  none: "Nenhum",
  minimal: "Mínimo",
  low: "Baixo",
  medium: "Médio",
  high: "Alto",
  xhigh: "Muito alto",
  max: "Máximo",
};

interface Preferences {
  locale: "pt-BR" | "en" | "es";
  unit: "mm" | "cm" | "m" | "in";
  gridVisible: boolean;
  snapEnabled: boolean;
  contours3d: boolean;
  hatch: "diagonal" | "cross" | "none";
  density: "comfortable" | "compact";
  lastEditorMode?: "plan" | "front" | "3d";
  viewportCompanion?: {
    docked: boolean;
    layout: { x: number; y: number; width: number; height: number };
  };
}

const sections: Array<{ id: SettingsSection; label: string; icon: typeof CircleUserRound }> = [
  { id: "general", label: "Perfil e aparência", icon: CircleUserRound },
  { id: "editor", label: "Editor e unidades", icon: SlidersHorizontal },
  { id: "cache", label: "Cache 3D", icon: Database },
  { id: "integrations", label: "Integrações", icon: Link2 },
  { id: "security", label: "Segurança", icon: Shield },
] as const;

export function SettingsPage({ scope = "workspace" }: { scope?: SettingsScope }) {
  const { session, refresh } = useAuth();
  const queryClient = useQueryClient();
  const [section, setSection] = useState<SettingsSection>(() => defaultSectionForScope(scope));
  const visibleSections = sections.filter(({ id }) => sectionIdsForScope(scope).includes(id));
  const preferencesQuery = useQuery({
    queryKey: ["preferences"],
    queryFn: () => api<{ data: Preferences }>("/preferences"),
  });
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [displayName, setDisplayName] = useState(session?.user.displayName ?? "");
  const [email, setEmail] = useState(session?.user.email ?? "");
  const [emailEditing, setEmailEditing] = useState(false);
  const profileEmail = email.trim() || session?.user.email || "";
  const profileEmailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileEmail);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [deletionPassword, setDeletionPassword] = useState("");
  const [deviceCode, setDeviceCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [cacheInfo, setCacheInfo] = useState({ usage: 0, quota: 0, entries: 0, updatedAt: null as Date | null });
  const sessionsQuery = useQuery({
    queryKey: ["sessions"],
    enabled: section === "security",
    queryFn: () => api<{ data: Array<{ id: string; current: boolean; device: string; lastUsedAt: string; expiresAt: string }> }>("/sessions"),
  });
  useEffect(() => {
    if (preferencesQuery.data) setPreferences(preferencesQuery.data.data);
  }, [preferencesQuery.data]);
  useEffect(() => {
    if (!session || !preferencesQuery.data) return;
    setDisplayName(session.user.displayName);
    setEmail(session.user.email);
  }, [preferencesQuery.data, session]);
  useEffect(() => {
    if (section === "cache") void refreshCacheInfo(setCacheInfo);
  }, [section]);
  const preferencesMutation = useMutation({
    mutationFn: (values: Preferences) => api<{ data: Preferences }>("/preferences", { method: "PATCH", body: values }),
    onSuccess: (response) => {
      queryClient.setQueryData(["preferences"], response);
    },
  });
  const generalMutation = useMutation({
    mutationFn: async () => {
      if (!preferences) throw new Error("As preferências ainda não foram carregadas.");
      const [profileResponse, preferencesResponse] = await Promise.all([
        api("/profile", {
          method: "PATCH",
          body: {
            displayName: displayName.trim(),
            email: profileEmail,
            locale: preferences.locale,
          },
        }),
        api<{ data: Preferences }>("/preferences", { method: "PATCH", body: preferences }),
      ]);
      return { profileResponse, preferencesResponse };
    },
    onSuccess: async ({ preferencesResponse }) => {
      queryClient.setQueryData(["preferences"], preferencesResponse);
      setEmailEditing(false);
      await refresh();
    },
  });
  const passwordMutation = useMutation({
    mutationFn: () => api("/profile", {
      method: "PATCH",
      body: {
        currentPassword: currentPassword || undefined,
        newPassword: newPassword || undefined,
      },
    }),
    onSuccess: async () => {
      setCurrentPassword("");
      setNewPassword("");
      await refresh();
    },
  });
  const revokeMutation = useMutation({
    mutationFn: (sessionId: string) => api(`/sessions/${sessionId}`, { method: "DELETE" }),
    onSuccess: () => sessionsQuery.refetch(),
  });
  const deviceMutation = useMutation({
    mutationFn: () => api<{ data: { code: string; expiresAt: string } }>("/device-codes", { method: "POST" }),
    onSuccess: (response) => setDeviceCode(response.data),
  });
  const deletionMutation = useMutation({
    mutationFn: () => api<{ data: { executeAfter: string } }>("/account-deletion-requests", { method: "POST", body: { password: deletionPassword } }),
  });
  const settingsSaving = section === "general" ? generalMutation.isPending : preferencesMutation.isPending;
  const settingsSaved = section === "general" ? generalMutation.isSuccess : preferencesMutation.isSuccess;
  const settingsError = section === "general" ? generalMutation.error : preferencesMutation.error;

  function saveSettings() {
    if (!preferences) return;
    if (section === "general") generalMutation.mutate();
    else preferencesMutation.mutate(preferences);
  }

  return (
    <div className="page settings-page">
      <header className="page-header page-header--compact">
        {scope === "account" ? (
          <div><span className="page-kicker">Sua conta</span><h1>Configurações do usuário</h1><p>Gerencie seu perfil, a aparência da interface e a segurança da sua conta.</p></div>
        ) : (
          <div><span className="page-kicker">Configurações do workspace</span><h1>Workspace</h1><p>Defina os padrões do editor, recursos locais e integrações deste ambiente.</p></div>
        )}
      </header>
      <div className="settings-layout">
        <nav className="settings-nav" aria-label={scope === "account" ? "Configurações do usuário" : "Configurações do workspace"}>
          {visibleSections.map(({ id, label, icon: Icon }) => (
            <button key={id} className={section === id ? "active" : ""} onClick={() => setSection(id)}>
              <Icon size={18} /><span>{label}</span><ChevronRight size={16} />
            </button>
          ))}
        </nav>
        <section className="settings-content">
          {section === "general" && (
            <>
              <SettingsHeading icon={CircleUserRound} title="Perfil e aparência" text="Sua identidade e suas preferências pessoais na interface." />
              <div className="settings-card profile-settings">
                <span className="avatar avatar--large">{session?.user.displayName.split(" ").map((part) => part[0]).slice(0, 2).join("")}</span>
                <div><strong>{session?.user.displayName}</strong><span>{session?.user.email}</span><small>{session?.roles.join(" · ")}</small></div>
              </div>
              {preferences && (
                <div className="settings-card settings-form">
                  <label><span>Nome de exibição<small>Usado na autoria e na auditoria.</small></span><input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={120} /></label>
                  <label><span>E-mail de acesso<small>Será usado para entrar na sua conta.</small></span><input name="profile-contact-address" type="text" inputMode="email" autoCapitalize="none" spellCheck={false} autoComplete="off" readOnly={!emailEditing} value={email || session?.user.email || ""} onFocus={() => setEmailEditing(true)} onChange={(event) => setEmail(event.target.value)} maxLength={254} data-1p-ignore data-lpignore="true" /></label>
                  <label><span>Idioma da interface<small>A troca é aplicada em toda a navegação.</small></span><select value={preferences.locale} onChange={(event) => setPreferences({ ...preferences, locale: event.target.value as Preferences["locale"] })}><option value="pt-BR">Português (Brasil)</option><option value="en">English</option><option value="es">Español</option></select></label>
                  <label><span>Densidade visual<small>Ajusta o espaçamento fora do editor técnico.</small></span><select value={preferences.density} onChange={(event) => setPreferences({ ...preferences, density: event.target.value as Preferences["density"] })}><option value="comfortable">Confortável</option><option value="compact">Compacta</option></select></label>
                </div>
              )}
            </>
          )}
          {section === "editor" && preferences && (
            <>
              <SettingsHeading icon={SlidersHorizontal} title="Editor e unidades" text="Defina os padrões para novos projetos." />
              <div className="settings-card settings-form">
                <label><span>Unidade de exibição<small>A cena permanece armazenada em milímetros.</small></span><select value={preferences.unit} onChange={(event) => setPreferences({ ...preferences, unit: event.target.value as Preferences["unit"] })}><option value="mm">Milímetros</option><option value="cm">Centímetros</option><option value="m">Metros</option><option value="in">Polegadas</option></select></label>
                <SettingSwitch label="Mostrar grade" text="Exibe a malha de apoio na planta." checked={preferences.gridVisible} onChange={(value) => setPreferences({ ...preferences, gridVisible: value })} />
                <SettingSwitch label="Snap inteligente" text="Prioriza endpoints, interseções e grade." checked={preferences.snapEnabled} onChange={(value) => setPreferences({ ...preferences, snapEnabled: value })} />
                <SettingSwitch label="Contornos 3D" text="Realça os limites dos objetos." checked={preferences.contours3d} onChange={(value) => setPreferences({ ...preferences, contours3d: value })} />
                <label><span>Hachura da planta<small>Padrão de áreas fechadas.</small></span><select value={preferences.hatch} onChange={(event) => setPreferences({ ...preferences, hatch: event.target.value as Preferences["hatch"] })}><option value="diagonal">Diagonal</option><option value="cross">Cruzada</option><option value="none">Sem hachura</option></select></label>
              </div>
            </>
          )}
          {section === "cache" && (
            <>
              <SettingsHeading icon={Database} title="Cache 3D" text="Gerencie derivados locais sem alterar seus projetos." />
              <div className="settings-card cache-card">
                <div className="cache-summary"><span><Database size={24} /></span><div><strong>{formatBytes(cacheInfo.usage)} em uso</strong><small>{cacheInfo.entries} arquivo(s) derivado(s) · {cacheInfo.updatedAt ? `atualizado às ${cacheInfo.updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "calculando…"}</small></div></div>
                <div className="cache-bar"><span style={{ width: `${cacheInfo.quota ? Math.min(100, cacheInfo.usage / cacheInfo.quota * 100) : 0}%` }} /></div>
                <div className="settings-actions">
                  <button className="button button--secondary" onClick={() => void refreshCacheInfo(setCacheInfo)}><RotateCcw size={17} /> Recalcular</button>
                  <button className="button button--danger-soft" onClick={async () => {
                    if ("caches" in window) {
                      const names = await caches.keys();
                      await Promise.all(names.map((name) => caches.delete(name)));
                    }
                    await refreshCacheInfo(setCacheInfo);
                  }}><Trash2 size={17} /> Limpar cache</button>
                </div>
              </div>
            </>
          )}
          {section === "integrations" && (
            <>
              <SettingsHeading icon={Link2} title="Integrações" text="Conecte ferramentas somente quando precisar." />
              <div className="integration-list">
                <IntegrationCard icon={Bluetooth} title="Trena Bluetooth" text="Capture medidas diretamente no editor." status={"bluetooth" in navigator ? "Disponível" : "Navegador incompatível"} onConfigure={() => void connectBluetooth()} />
                <IntegrationCard icon={Cloud} title="SketchUp" text="Exportação e extensão para o seu fluxo 3D." status="Em preparação" disabled />
                <IntegrationCard icon={Gauge} title="MCP" text="Autorize dispositivos com código temporário." status={deviceCode ? `Código ${deviceCode.code}` : "Nenhum código ativo"} onConfigure={() => deviceMutation.mutate()} />
              </div>
              {deviceCode && <div className="settings-card device-code"><strong>{deviceCode.code}</strong><span>Expira às {new Date(deviceCode.expiresAt).toLocaleTimeString("pt-BR")}. Compartilhe somente com o dispositivo que está autorizando.</span></div>}
            </>
          )}
          {section === "security" && (
            <>
              <SettingsHeading icon={Shield} title="Segurança da conta" text="Sessões, acessos e ações sensíveis." />
              <div className="settings-card security-card">
                <div><span className="security-ok"><Check size={18} /></span><span><strong>Sessão atual protegida</strong><small>Cookie HttpOnly · expiração rotativa · CSRF ativo</small></span></div>
              </div>
              <div className="settings-card settings-form">
                <h3><KeyRound size={18} /> Alterar senha</h3>
                <label><span>Senha atual</span><input type="password" autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
                <label><span>Nova senha<small>Mínimo de 12 caracteres.</small></span><input type="password" autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
                <div className="settings-actions"><button className="button button--secondary" onClick={() => passwordMutation.mutate()} disabled={currentPassword.length < 8 || newPassword.length < 12 || passwordMutation.isPending}>Atualizar senha</button></div>
              </div>
              <div className="settings-card active-sessions">
                <h3>Sessões ativas</h3>
                {sessionsQuery.data?.data.map((activeSession) => (
                  <div key={activeSession.id}>
                    <span><strong>{activeSession.device}</strong><small>Último uso {new Date(activeSession.lastUsedAt).toLocaleString("pt-BR")} · expira {new Date(activeSession.expiresAt).toLocaleDateString("pt-BR")}</small></span>
                    {activeSession.current ? <em>Esta sessão</em> : <button className="button button--danger-soft" onClick={() => revokeMutation.mutate(activeSession.id)}>Revogar</button>}
                  </div>
                ))}
              </div>
              <div className="danger-zone">
                <div><strong>Excluir minha conta</strong><p>Inicia um período de carência de 30 dias. Confirme sua senha para continuar.</p>{deletionMutation.isSuccess && <small>Solicitação registrada. Você ainda pode contatar o administrador durante a carência.</small>}</div>
                <span className="danger-confirm"><input type="password" aria-label="Senha para excluir a conta" placeholder="Senha atual" value={deletionPassword} onChange={(event) => setDeletionPassword(event.target.value)} /><button className="button button--danger-soft" onClick={() => deletionMutation.mutate()} disabled={deletionPassword.length < 8 || deletionMutation.isPending}><UserX size={16} /> Solicitar exclusão</button></span>
              </div>
            </>
          )}
          {preferences && ["general", "editor"].includes(section) && (
            <div className="settings-savebar">
              {settingsSaved && <span className="save-success"><Check size={16} /> {section === "general" ? "Perfil e preferências salvos" : "Preferências salvas"}</span>}
              {settingsError && <span className="form-error">{settingsError instanceof ApiError ? settingsError.message : "Não foi possível salvar."}</span>}
              <button className="button button--primary" onClick={saveSettings} disabled={settingsSaving || (section === "general" && (!displayName.trim() || !profileEmailIsValid))}><Save size={17} /> {settingsSaving ? "Salvando…" : "Salvar alterações"}</button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function SettingsHeading({ icon: Icon, title, text }: { icon: typeof Palette; title: string; text: string }) {
  return <header className="settings-heading"><span><Icon size={21} /></span><div><h2>{title}</h2><p>{text}</p></div></header>;
}

/**
 * A conexão com o modelo.
 *
 * A chave é gravada uma vez e nunca mais volta para cá — o campo mostra só o
 * começo e o fim dela. Por isso salvar sem digitar nada mantém a que já existe:
 * a tela não teria como reenviar o que nunca recebeu.
 *
 * O endereço vem preenchido com o padrão do provedor escolhido e continua
 * editável, porque quem usa um gateway ou um proxy corporativo precisa apontar
 * para outro lugar.
 */
export function AiConnectionSettings() {
  const query = useQuery({ queryKey: ["ai-settings"], queryFn: () => api<{ data: AiSettings }>("/ai-settings") });
  const stored = query.data?.data;
  const [form, setForm] = useState<{
    provider: AiProvider;
    baseUrl: string;
    model: string;
    effort: AiEffort | "";
    apiKey: string;
    mcpServers: McpServerForm[];
  } | null>(null);
  const [replacingKey, setReplacingKey] = useState(false);
  const [fetchedModels, setFetchedModels] = useState<AiModelInfo[] | null>(null);
  const [testResult, setTestResult] = useState("");

  useEffect(() => {
    if (stored && !form) {
      setForm({
        provider: stored.provider,
        baseUrl: stored.baseUrl,
        model: stored.model,
        effort: stored.effort ?? "",
        apiKey: "",
        mcpServers: stored.mcpServers.map((server) => ({
          id: server.id,
          name: server.name,
          url: server.url,
          enabled: server.enabled,
          allowedTools: server.allowedTools.join(", "),
          authorizationToken: "",
          tokenMask: server.tokenMask,
        })),
      });
    }
  }, [stored, form]);

  const save = useMutation({
    mutationFn: () => api("/ai-settings", {
      method: "PUT",
      body: {
        provider: form!.provider,
        baseUrl: form!.baseUrl.trim(),
        model: form!.model.trim(),
        effort: form!.effort || null,
        apiKey: form!.apiKey.trim() || undefined,
        mcpServers: form!.mcpServers.map((server) => ({
          id: server.id,
          name: server.name.trim(),
          url: server.url.trim(),
          enabled: server.enabled,
          allowedTools: server.allowedTools.split(",").map((value) => value.trim()).filter(Boolean),
          authorizationToken: server.authorizationToken.trim() || undefined,
        })),
      },
    }),
    onSuccess: async () => {
      setForm((value) => (value ? { ...value, apiKey: "" } : value));
      setReplacingKey(false);
      setTestResult("");
      await query.refetch();
    },
  });
  const test = useMutation({
    mutationFn: () => api<{ data: { ok: boolean; reply: string } }>("/ai-settings/test", { method: "POST" }),
    onSuccess: (result) => setTestResult(`Conectado. O modelo respondeu: “${result.data.reply}”`),
    onError: () => setTestResult(""),
  });
  const loadModels = useMutation({
    mutationFn: () => api<{ data: { models: AiModelInfo[] } }>("/ai-models"),
    onSuccess: (result) => {
      setFetchedModels(result.data.models);
      setForm((value) => {
        if (!value) return value;
        const selected = result.data.models.find((model) => model.id === value.model);
        return {
          ...value,
          effort: compatibleModelEffort(value.provider, value.model, value.effort, selected?.supportedEfforts) ?? "",
        };
      });
    },
  });

  if (!stored || !form) {
    return (
      <>
        <SettingsHeading icon={Sparkles} title="Inteligência artificial" text="Configure o modelo usado pelo meX em todos os workspaces da plataforma." />
        <div className="settings-card"><span>Carregando…</span></div>
      </>
    );
  }

  const provider = stored.providers[form.provider];
  // A lista que o próprio provedor devolveu vale mais do que a nossa: modelo
  // novo aparece lá no dia em que sai, sem depender de alguém atualizar o código.
  const modelOptions = [...new Set([...(fetchedModels ?? []).map((model) => model.id), ...provider.models, form.model].filter(Boolean))];
  const selectedModelInfo = fetchedModels?.find((model) => model.id === form.model)
    ?? (stored.currentModelMetadata?.id === form.model ? stored.currentModelMetadata : undefined);
  const effortOptions = selectedModelInfo?.supportedEfforts
    ?? modelEffortLevels(form.provider, form.model);

  return (
    <>
      <SettingsHeading icon={Sparkles} title="Inteligência artificial" text="Configure o modelo usado pelo meX em todos os workspaces da plataforma." />
      <div className="settings-card settings-form ai-settings-form">
        <p className="ai-settings-note ai-settings-form__wide">
          Esta conexão é global. Depois de salva, todos os usuários da plataforma usam o mesmo modelo no meX sem precisar configurar uma chave por workspace.
        </p>
        <label>
          <span>Provedor<small>Define o endereço padrão e o formato da conversa.</small></span>
          <select
            value={form.provider}
            onChange={(event) => {
              const next = event.target.value as AiProvider;
              setFetchedModels(null);
              setTestResult("");
              // Trocar de empresa troca o endereço e o modelo junto: manter o
              // endereço da anterior só produziria um erro difícil de ler.
              const model = stored.providers[next].models[0] ?? "";
              setForm({
                ...form,
                provider: next,
                baseUrl: stored.providers[next].baseUrl,
                model,
                effort: compatibleModelEffort(next, model, null) ?? "",
              });
            }}
          >
            {(Object.keys(stored.providers) as AiProvider[]).map((key) => (
              <option key={key} value={key}>{stored.providers[key].label}</option>
            ))}
          </select>
        </label>

        {selectedModelInfo && (
          <div className="ai-settings-note ai-settings-form__wide">
            Janela informada pelo provedor: {selectedModelInfo.maxInputTokens?.toLocaleString("pt-BR") ?? "não informada"} tokens · ferramentas {selectedModelInfo.supportsTools ? "compatíveis" : "indisponíveis"} · contexto de servidor {selectedModelInfo.supportsContextManagement ? "disponível" : "indisponível"}.
          </div>
        )}

        <label>
          <span>Endereço da API<small>Vem preenchido com o padrão do provedor; troque se usar um gateway.</small></span>
          <input value={form.baseUrl} onChange={(event) => setForm({ ...form, baseUrl: event.target.value })} placeholder={provider.baseUrl} />
        </label>

        <div className="ai-settings-form__wide">
          <h3>Servidores MCP externos</h3>
          <p className="ai-settings-note">
            {form.provider === "anthropic"
              ? "O agente pode consultar fornecedores, armazenamento e ERP. Resultados externos são tratados como dados não confiáveis."
              : "MCP externo e compactação de servidor exigem Anthropic. Na OpenAI, o agente continua com as capacidades internas e compactação local."}
          </p>
          {form.mcpServers.map((server, index) => (
            <div className="settings-card settings-form" key={server.id}>
              <SettingSwitch label="Ativo" text="Disponibiliza este servidor ao agente." checked={server.enabled} onChange={(enabled) => setForm({ ...form, mcpServers: form.mcpServers.map((item, itemIndex) => itemIndex === index ? { ...item, enabled } : item) })} />
              <label><span>Nome</span><input value={server.name} onChange={(event) => setForm({ ...form, mcpServers: form.mcpServers.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item) })} /></label>
              <label><span>URL HTTPS</span><input value={server.url} onChange={(event) => setForm({ ...form, mcpServers: form.mcpServers.map((item, itemIndex) => itemIndex === index ? { ...item, url: event.target.value } : item) })} /></label>
              <label><span>Token de autorização<small>{server.tokenMask ? `Salvo como ${server.tokenMask}; deixe vazio para manter.` : "Cifrado no servidor e nunca devolvido."}</small></span><input type="password" autoComplete="off" value={server.authorizationToken} onChange={(event) => setForm({ ...form, mcpServers: form.mcpServers.map((item, itemIndex) => itemIndex === index ? { ...item, authorizationToken: event.target.value } : item) })} /></label>
              <label><span>Ferramentas permitidas<small>Opcional, separadas por vírgula.</small></span><input value={server.allowedTools} onChange={(event) => setForm({ ...form, mcpServers: form.mcpServers.map((item, itemIndex) => itemIndex === index ? { ...item, allowedTools: event.target.value } : item) })} /></label>
              <button type="button" className="button button--danger-soft" onClick={() => setForm({ ...form, mcpServers: form.mcpServers.filter((_, itemIndex) => itemIndex !== index) })}>Remover servidor</button>
            </div>
          ))}
          <button type="button" className="button button--secondary" disabled={form.provider !== "anthropic" || form.mcpServers.length >= 8} onClick={() => {
            const id = `mcp_${Date.now().toString(36)}`;
            setForm({ ...form, mcpServers: [...form.mcpServers, { id, name: "", url: "https://", enabled: true, allowedTools: "", authorizationToken: "" }] });
          }}>Adicionar servidor MCP</button>
        </div>

        <label>
          <span>Modelo<small>Escolha da lista ou digite o nome exato.</small></span>
          <input
            list="ai-model-options"
            value={form.model}
            onChange={(event) => {
              const model = event.target.value;
              const metadata = fetchedModels?.find((item) => item.id === model);
              setForm({
                ...form,
                model,
                effort: compatibleModelEffort(form.provider, model, form.effort, metadata?.supportedEfforts) ?? "",
              });
            }}
            placeholder={provider.models[0]}
          />
          <datalist id="ai-model-options">
            {modelOptions.map((model) => <option key={model} value={model} />)}
          </datalist>
        </label>

        <label>
          <span>Effort<small>Controla profundidade, tempo e consumo de tokens neste modelo.</small></span>
          <select
            value={form.effort}
            disabled={effortOptions.length === 0}
            onChange={(event) => setForm({ ...form, effort: event.target.value as AiEffort })}
          >
            {effortOptions.length === 0
              ? <option value="">Não disponível para este modelo</option>
              : effortOptions.map((effort) => <option key={effort} value={effort}>{effortLabels[effort]} ({effort})</option>)}
          </select>
        </label>

        <label className="ai-settings-form__wide">
          <span>Chave de API<small>Enquanto você digita, a chave fica visível. Depois de salva, mostramos somente o início e o final.</small></span>
          {stored.hasKey && !replacingKey ? (
            <div className="ai-key-row">
              <input
                className="ai-key-display"
                aria-label="Chave de API salva, parcialmente ocultada"
                readOnly
                value={stored.keyMask}
              />
              <button type="button" className="button button--secondary" onClick={() => setReplacingKey(true)}>Substituir</button>
            </div>
          ) : (
            <div className="ai-key-row">
              <input
                type="text"
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={form.apiKey}
                onChange={(event) => setForm({ ...form, apiKey: event.target.value })}
                placeholder={form.provider === "anthropic" ? "sk-ant-…" : "sk-…"}
              />
              {stored.hasKey && (
                <button type="button" className="button button--secondary" onClick={() => { setReplacingKey(false); setForm({ ...form, apiKey: "" }); }}>Cancelar</button>
              )}
            </div>
          )}
        </label>

        <div className="settings-actions ai-settings-form__wide">
          <button
            className="button button--primary"
            onClick={() => save.mutate()}
            disabled={save.isPending || !form.baseUrl.trim() || !form.model.trim() || (!stored.hasKey && !form.apiKey.trim())}
          >
            <Save size={17} /> {save.isPending ? "Salvando…" : "Salvar conexão"}
          </button>
          <button className="button button--secondary" onClick={() => loadModels.mutate()} disabled={!stored.hasKey || loadModels.isPending}>
            <RotateCcw size={17} /> {loadModels.isPending ? "Buscando…" : "Buscar modelos"}
          </button>
          <button className="button button--secondary" onClick={() => test.mutate()} disabled={!stored.hasKey || test.isPending}>
            <Check size={17} /> {test.isPending ? "Testando…" : "Testar conexão"}
          </button>
        </div>

        {testResult && <p className="ai-settings-ok ai-settings-form__wide">{testResult}</p>}
        {fetchedModels && (
          <p className="ai-settings-note ai-settings-form__wide">
            {fetchedModels.length} modelos disponíveis nesta conta — limites e capacidades vieram da API do provedor.
          </p>
        )}
        <MutationError error={save.error ?? test.error ?? loadModels.error ?? query.error} />
      </div>
    </>
  );
}

function MutationError({ error }: { error: unknown }) {
  if (!error) return null;
  return <span className="form-error ai-settings-form__wide">{error instanceof ApiError ? error.message : "Não foi possível concluir."}</span>;
}

async function refreshCacheInfo(
  update: React.Dispatch<React.SetStateAction<{ usage: number; quota: number; entries: number; updatedAt: Date | null }>>,
): Promise<void> {
  const estimate = await navigator.storage?.estimate?.().catch(() => ({ usage: 0, quota: 0 }));
  let entries = 0;
  if ("caches" in window) {
    const names = await caches.keys();
    const cachedRequests = await Promise.all(names.map(async (name) => (await caches.open(name)).keys()));
    entries = cachedRequests.reduce((total, requests) => total + requests.length, 0);
  }
  update({
    usage: estimate?.usage ?? 0,
    quota: estimate?.quota ?? 0,
    entries,
    updatedAt: new Date(),
  });
}

function formatBytes(value: number): string {
  if (value < 1_024) return `${value} B`;
  if (value < 1_048_576) return `${(value / 1_024).toFixed(1)} KB`;
  if (value < 1_073_741_824) return `${(value / 1_048_576).toFixed(1)} MB`;
  return `${(value / 1_073_741_824).toFixed(1)} GB`;
}

function SettingSwitch({ label, text, checked, onChange }: { label: string; text: string; checked: boolean; onChange(value: boolean): void }) {
  return <label className="settings-switch"><span>{label}<small>{text}</small></span><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} /><i /></label>;
}

function IntegrationCard({ icon: Icon, title, text, status, onConfigure, disabled }: { icon: typeof Bluetooth; title: string; text: string; status: string; onConfigure?(): void; disabled?: boolean }) {
  return <article className="integration-card"><span className="integration-card__icon"><Icon size={21} /></span><div><strong>{title}</strong><p>{text}</p><small>{status}</small></div><button className="button button--secondary" onClick={onConfigure} disabled={disabled || !onConfigure}>{disabled ? "Em preparação" : "Configurar"}</button></article>;
}

async function connectBluetooth(): Promise<void> {
  const bluetooth = (navigator as Navigator & {
    bluetooth?: { requestDevice(options: { acceptAllDevices: boolean; optionalServices: string[] }): Promise<unknown> };
  }).bluetooth;
  if (!bluetooth) return;
  await bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: ["0000181a-0000-1000-8000-00805f9b34fb"],
  }).catch(() => undefined);
}
